import React from "react";
import { AlertTriangle, ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";
import TermTooltip from "@/components/TermTooltip";
import { useApp } from "@/context/AppContext";

const BIAS_COLORS = {
  trend_following: { chip: "chip-up", label: "Suivi de tendance" },
  mean_reversion: { chip: "chip-neutral", label: "Retour à la moyenne" },
  no_trade: { chip: "chip-down", label: "PAS DE TRADING" },
};

export default function RegimePanel({ regime, htf }) {
  const { t } = useApp();
  if (!regime) return null;
  const bias = BIAS_COLORS[regime.trade_bias] || BIAS_COLORS.trend_following;
  const Icon = regime.quality_score >= 75 ? ShieldCheck :
               regime.quality_score >= 40 ? ShieldAlert : ShieldX;
  const iconColor = regime.quality_score >= 75 ? "text-up" :
                    regime.quality_score >= 40 ? "text-warn" : "text-down";

  return (
    <div className="panel p-4 space-y-3" data-testid="regime-panel">
      <div className="flex items-center justify-between">
        <div className="panel-title text-[14px]">{t("regime.title", "Régime de marché")}</div>
        <Icon size={16} className={iconColor} />
      </div>

      <div className="text-lg font-heading" data-testid="regime-label">{regime.label_fr}</div>
      <div className="flex items-center gap-2">
        <span className={`chip ${bias.chip}`} data-testid="regime-bias">{bias.label}</span>
        <span className="chip">{t("regime.quality", "Qualité")} {regime.quality_score}/100</span>
      </div>

      <div className="grid grid-cols-3 gap-2 pt-2">
        <Stat label={<TermTooltip term="ADX">ADX(14)</TermTooltip>} value={regime.adx} />
        <Stat label={<TermTooltip term="HURST">Hurst</TermTooltip>} value={regime.hurst} />
        <Stat label="Vol %ile" value={`${regime.vol_percentile}`} />
      </div>

      {htf && Object.keys(htf.parents || {}).length > 0 && (
        <div className="pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">{t("regime.htf_context", "Contexte supérieur (HTF)")}</div>
          <div className="space-y-1.5">
            {Object.entries(htf.parents).map(([tf, b]) => (
              <div key={tf} className="flex items-center justify-between text-xs">
                <span className="font-mono text-white/60">{tf}</span>
                <span className={
                  b.current === "Haussière" ? "text-up" :
                  b.current === "Baissière" ? "text-down" : "text-warn"
                }>
                  {b.current === "Haussière" ? t("trend.bullish", "Haussière") :
                   b.current === "Baissière" ? t("trend.bearish", "Baissière") :
                   t("trend.neutral", "Neutre")} · {b.confidence}%
                </span>
              </div>
            ))}
            <div className="pt-2 flex items-center justify-between text-xs">
              <span className="text-white/50 uppercase tracking-wider text-[10px]">{t("regime.dominant", "Biais dominant")}</span>
              <span className={`chip ${
                htf.dominant_bias === "Haussière" ? "chip-up" :
                htf.dominant_bias === "Baissière" ? "chip-down" : "chip-neutral"
              }`}>{htf.dominant_bias === "Haussière" ? t("trend.bullish") :
                    htf.dominant_bias === "Baissière" ? t("trend.bearish") :
                    t("trend.neutral")}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-hover rounded px-2 py-1.5 border border-white/5">
      <div className="text-[9px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="font-mono text-sm">{value}</div>
    </div>
  );
}
